import { getChEventsAnalyticsUrl } from '@/libs/analytics/ch-events-config';
import {
    DwAmplitudeEvent,
    DwAmplitudeInstance,
    createDwAmplitudeInstance,
} from '@/libs/analytics/data-warehouse/amplitude';
import useAppConfig from '@rhg/hooks/use-app-config';
import { logger } from '@rhg/libs/logger';
import {
    createContext,
    PropsWithChildren,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

export interface DwAmplitudeContextValue {
    trackDwEvent: (event: DwAmplitudeEvent) => Promise<void>;
    isEnabled: boolean;
    isInitialized: boolean;
}

const defaultContext: DwAmplitudeContextValue = {
    trackDwEvent: async () => {},
    isEnabled: false,
    isInitialized: false,
};

export const DwAmplitudeContext =
    createContext<DwAmplitudeContextValue>(defaultContext);

export function DwAmplitudeProvider({ children }: PropsWithChildren) {
    const {
        NEXT_PUBLIC_FF_DW_AMPLITUDE_ENABLED: dwAmplitudeEnabled,
        NEXT_PUBLIC_AMPLITUDE_CH_EVENTS_PROXY_URL: chEventsProxyUrl,
        NEXT_PUBLIC_AMPLITUDE_TOKEN: amplitudeToken,
    } = useAppConfig();

    const isEnabled =
        dwAmplitudeEnabled && !!chEventsProxyUrl && !!amplitudeToken;
    const instanceRef = useRef<DwAmplitudeInstance | null>(null);
    const initGuardRef = useRef(false);
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        if (!isEnabled || initGuardRef.current) return;

        const initInstance = async () => {
            try {
                // DW-only forwarding intentionally bypasses the consent-gated
                // frontend analytics init path. These events replace legacy DW
                // backend delivery during migration and must continue flowing
                // even when the frontend Amplitude client is opted out.
                const serverUrl = getChEventsAnalyticsUrl(chEventsProxyUrl);
                const instance = createDwAmplitudeInstance(
                    amplitudeToken,
                    serverUrl
                );
                // One-shot: set ref before await to prevent double-init under
                // React Strict Mode. Init failure is non-retryable (bad token,
                // network) — events silently no-op for the page session.
                initGuardRef.current = true;
                await instance.init();
                instanceRef.current = instance;
                setIsInitialized(true);
            } catch (error) {
                logger.error(
                    'Failed to initialize DW Amplitude instance',
                    {},
                    error instanceof Error ? error : new Error(String(error))
                );
            }
        };

        void initInstance();
    }, [isEnabled, amplitudeToken, chEventsProxyUrl]);

    const trackDwEvent = useCallback(
        async (event: DwAmplitudeEvent) => {
            if (!isEnabled || !instanceRef.current) return;

            try {
                await instanceRef.current.trackDwEvent(event);
            } catch (error) {
                logger.error(
                    'Failed to track DW Amplitude event',
                    {},
                    error instanceof Error ? error : new Error(String(error))
                );
            }
        },
        [isEnabled]
    );

    return (
        <DwAmplitudeContext.Provider
            value={{
                trackDwEvent,
                isEnabled,
                isInitialized,
            }}
        >
            {children}
        </DwAmplitudeContext.Provider>
    );
}
